<template>
  <hb-section :title="$t('influencer_panel.menu_campaigns')" :subtitle="summary">
    <template #actions>
      <router-link :to="{ name: 'influencer-panel-campaigns' }" class="campaign-compact__all">
        {{ $t('influencer_panel.see_all') }}
        <feather-icon icon="ChevronRightIcon" size="14" />
      </router-link>
    </template>

    <hb-empty-state
      v-if="!participations.length"
      :title="$t('influencer_panel.no_campaigns')"
      icon="TrendingUpIcon"
    />

    <div v-else class="campaign-compact__list">
      <div v-for="item in participations" :key="item.id" class="campaign-compact__row">
        <div class="campaign-compact__identity">
          <div class="campaign-compact__name">{{ campaignName(item) }}</div>
          <div class="campaign-compact__entity">{{ entityLine(item) }}</div>
        </div>

        <div class="campaign-compact__stat campaign-compact__stat--commission">
          <div class="campaign-compact__stat-label">{{ $t('influencer_panel.commission') }}</div>
          <div class="campaign-compact__stat-value">{{ commissionLabel(item) }}</div>
        </div>

        <div class="campaign-compact__stat">
          <div class="campaign-compact__stat-label">{{ $t('influencer_panel.sales') }}</div>
          <div class="campaign-compact__stat-value">{{ metric(item, 'conversions') }}</div>
        </div>

        <div class="campaign-compact__stat campaign-compact__stat--earned">
          <div class="campaign-compact__stat-label">{{ $t('influencer_panel.you_earned') }}</div>
          <div class="campaign-compact__stat-value">
            <hb-money :cents="metric(item, 'commission_cents')" tone="brand" />
          </div>
        </div>

        <div v-if="item.coupon_code" class="campaign-compact__coupon" :title="$t('influencer_panel.coupon_label')">
          <feather-icon icon="TagIcon" size="13" />
          <code>{{ item.coupon_code }}</code>
          <hb-copy-button :text="item.coupon_code" :allow-share="false" />
        </div>

        <div class="campaign-compact__badge">
          <b-badge v-if="endingSoon(item)" variant="light-warning">
            {{ $t('influencer_panel.ends_in_days', { days: item.days_to_end }) }}
          </b-badge>
          <hb-status-badge v-else :status="item.status" domain="participation" />
        </div>

        <div class="campaign-compact__actions">
          <hb-icon-button
            icon="Link2Icon"
            tone="primary"
            :label="$t('influencer_panel.generate_link')"
            @click="$emit('generate-link', item)"
          />
          <hb-icon-button
            icon="ImageIcon"
            tone="warning"
            :label="$t('influencer_panel.material_title')"
            @click="$emit('material', item)"
          />
          <hb-icon-button
            icon="InfoIcon"
            tone="success"
            :label="$t('influencer_panel.info_title')"
            @click="$emit('info', item)"
          />
        </div>
      </div>
    </div>
  </hb-section>
</template>

<script>
import { BBadge } from 'bootstrap-vue'
import HbSection from '@/components/hubee/HbSection.vue'
import HbMoney from '@/components/hubee/HbMoney.vue'
import HbStatusBadge from '@/components/hubee/HbStatusBadge.vue'
import HbIconButton from '@/components/hubee/HbIconButton.vue'
import HbEmptyState from '@/components/hubee/HbEmptyState.vue'
import HbCopyButton from '@/components/hubee/HbCopyButton.vue'
import { campaignName, entityLine, commissionLabel, participationMetric } from '../campaign'

/** Resumo das campanhas ativas no Resumo — a versao completa vive em Minhas Campanhas. */
export default {
  name: 'CampaignCompactList',
  components: {
    BBadge, HbSection, HbMoney, HbStatusBadge, HbIconButton, HbEmptyState, HbCopyButton,
  },
  props: {
    participations: { type: Array, default: () => [] },
    totalSales: { type: Number, default: 0 },
  },
  computed: {
    summary() {
      const active = this.participations.filter(p => p.status === 'AC').length

      return `${this.$t('influencer_panel.active_count', { count: active })} · ${this.$t('influencer_panel.sales_in_period', { count: this.totalSales })}`
    },
  },
  methods: {
    campaignName,
    entityLine,
    commissionLabel,
    metric(item, key) {
      return participationMetric(item, key)
    },
    endingSoon(item) {
      return item.status === 'AC' && item.days_to_end !== null
        && item.days_to_end !== undefined && item.days_to_end >= 0 && item.days_to_end <= 7
    },
  },
}
</script>

<style scoped>
.campaign-compact__all {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.9rem;
  font-weight: var(--fw-medium);
}

.campaign-compact__row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 1rem;
  padding: 0.9rem 0.75rem;
  border-radius: var(--radius);
  border-bottom: 1px solid var(--border-color);
  transition: background-color var(--dur-fast) var(--ease-in-out);
}

.campaign-compact__row:last-child {
  border-bottom: none;
}

.campaign-compact__row:hover {
  background: var(--surface-hover);
}

.campaign-compact__identity {
  flex: 1 1 220px;
  min-width: 0;
}

.campaign-compact__name {
  font-weight: var(--fw-medium);
  color: var(--text-heading);
}

.campaign-compact__entity {
  font-size: var(--fs-sm);
  color: var(--text-muted);
}

.campaign-compact__stat {
  flex: 0 0 auto;
  min-width: 64px;
}

.campaign-compact__stat--commission { min-width: 74px; }
.campaign-compact__stat--earned { min-width: 104px; }

.campaign-compact__stat-label {
  font-size: var(--fs-xs);
  color: var(--text-muted);
}

.campaign-compact__stat-value {
  font-weight: var(--fw-medium);
  color: var(--text-heading);
}

.campaign-compact__coupon {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: var(--fs-sm);
  color: var(--text-muted);
}

.campaign-compact__coupon code {
  color: var(--text-heading);
  font-weight: var(--fw-medium);
}

.campaign-compact__badge {
  flex: 0 0 auto;
}

.campaign-compact__actions {
  flex: 0 0 auto;
  display: flex;
  gap: 0.35rem;
  margin-left: auto;
}
</style>
